import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth } from "../contexts/AuthContext.js";
import "./CoachDashboard.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const CoachDashboard = () => {
  const { user } = useAuth();
  const [coach, setCoach] = useState(null);
  const [students, setStudents] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAvailability, setEditingAvailability] = useState(false);

  const days = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
  const hours = Array.from({ length: 12 }, (_, i) => i + 10); // 10-21

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Get coach data linked to this user
      const coachRes = await axios.get(`${API_URL}/api/coaches/${user.coachId}`);
      setCoach(coachRes.data);

      // Get all students to show in schedule
      const studentsRes = await axios.get(`${API_URL}/api/students`);
      setStudents(studentsRes.data);

      // Get schedule
      const scheduleRes = await axios.get(`${API_URL}/api/schedule`);
      setSchedule(scheduleRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAvailability = (day, hour) => {
    if (!editingAvailability) return;

    const timeSlot = `${day} ${hour}`;
    const currentAvailability = coach.availableTimes || [];

    const newAvailability = currentAvailability.includes(timeSlot)
      ? currentAvailability.filter(t => t !== timeSlot)
      : [...currentAvailability, timeSlot];

    setCoach({ ...coach, availableTimes: newAvailability });
  };

  const handleSaveAvailability = async () => {
    try {
      await axios.put(`${API_URL}/api/coaches/${coach._id}`, {
        availableTimes: coach.availableTimes
      });
      toast.success("Verfügbarkeit gespeichert");
      setEditingAvailability(false);
      fetchData();
    } catch (error) {
      console.error("Error saving availability:", error);
      toast.error("Fehler beim Speichern");
    }
  };

  const handleCancelEdit = () => {
    setEditingAvailability(false);
    fetchData(); // Reload original data
  };

  const getStudentsInCell = (day, hour) => {
    const cell = schedule.find(c => c.day === day && c.hour === hour);
    if (!cell || !cell.students) return [];

    return cell.students
      .map(id => students.find(s => s._id === id))
      .filter(s => {
        if (!s) return false;
        // Only show students assigned to this coach
        if (Array.isArray(s.assignments) && s.assignments.length > 0) {
          return s.assignments.some(a =>
            a.day === day &&
            a.hour === hour &&
            String(a.coach) === String(coach._id)
          );
        }
        // Fallback to legacy fields
        return String(s.coach) === String(coach._id);
      });
  };

  const isAvailable = (day, hour) => {
    if (!coach || !coach.availableTimes) return false;
    return coach.availableTimes.includes(`${day} ${hour}`);
  };

  if (loading) {
    return <div className="loading">Laden...</div>;
  }

  if (!coach) {
    return <div className="error">Trainer-Daten nicht gefunden</div>;
  }

  return (
    <div className="coach-dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Trainer Dashboard</h1>
          <p className="welcome-text">
            Willkommen, {coach.firstName} {coach.lastName}
          </p>
        </div>
        <div className="header-actions">
          {!editingAvailability ? (
            <button
              className="btn-edit-availability"
              onClick={() => setEditingAvailability(true)}
            >
              ✏️ Verfügbarkeit bearbeiten
            </button>
          ) : (
            <div className="edit-actions">
              <button className="btn-save" onClick={handleSaveAvailability}>
                ✅ Speichern
              </button>
              <button className="btn-cancel" onClick={handleCancelEdit}>
                ❌ Abbrechen
              </button>
            </div>
          )}
        </div>
      </div>

      {editingAvailability && (
        <div className="edit-mode-notice">
          🔧 Bearbeitungsmodus: Klicken Sie auf Zellen, um Ihre Verfügbarkeit zu ändern
        </div>
      )}

      <div className="schedule-container">
        <table className="schedule-table">
          <thead>
            <tr>
              <th>Uhrzeit</th>
              {days.map(day => (
                <th key={day}>{day}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {hours.map(hour => (
              <tr key={hour}>
                <td className="time-cell">{hour}:00</td>
                {days.map(day => {
                  const available = isAvailable(day, hour);
                  const studentsHere = getStudentsInCell(day, hour);

                  return (
                    <td
                      key={`${day}-${hour}`}
                      className={`schedule-cell ${available ? "available" : "unavailable"} ${
                        editingAvailability ? "editable" : ""
                      } ${studentsHere.length > 0 ? "has-students" : ""}`}
                      onClick={() => handleToggleAvailability(day, hour)}
                    >
                      {studentsHere.length > 0 && (
                        <div className="students-list">
                          {studentsHere.map(student => (
                            <div key={student._id} className="student-item">
                              {student.firstName} {student.lastName}
                              {student.adult ? " 👤" : " 👶"}
                            </div>
                          ))}
                        </div>
                      )}
                      {!available && studentsHere.length === 0 && (
                        <div className="unavailable-text">-</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="dashboard-info">
        <div className="info-card">
          <h3>📊 Statistiken</h3>
          <div className="stat-item">
            <span className="stat-label">Verfügbare Stunden:</span>
            <span className="stat-value">{coach.availableTimes?.length || 0}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Aktive Kurse:</span>
            <span className="stat-value">
              {schedule.filter(cell => {
                const studentsHere = getStudentsInCell(cell.day, cell.hour);
                return studentsHere.length > 0;
              }).length}
            </span>
          </div>
        </div>

        <div className="info-card">
          <h3>ℹ️ Trainer-Info</h3>
          <div className="info-item">
            <span className="info-label">E-Mail:</span>
            <span className="info-value">{coach.email || "-"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Telefon:</span>
            <span className="info-value">{coach.phone || "-"}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachDashboard;
